import React, { useEffect, useRef, useState } from "react";
import { useCollabSession } from "./CollabSessionContext";
import { usePresence } from "./usePresence";
import { toFractions, toPixels, type ScrollMetrics } from "./pointerMath";

const PUBLISH_INTERVAL_MS = 30;
const POINTER_TTL_MS = 3000;

const readScroll = (el: Element): ScrollMetrics => ({
    scrollLeft: el.scrollLeft,
    scrollTop: el.scrollTop,
    scrollWidth: el.scrollWidth,
    scrollHeight: el.scrollHeight
});

/**
 * Figma-style remote mouse pointers. Mount inside a `position: relative`
 * container that also contains the editor; the editor's scrollable box is
 * found via [data-collab-scroll]. Publishes the local pointer (throttled)
 * and renders remote pointers for peers viewing `activeField`.
 */
const PointerOverlay: React.FC<{ activeField?: string }> = ({ activeField }) => {
    const session = useCollabSession();
    const peers = usePresence({ includePointers: true });
    const overlayRef = useRef<HTMLDivElement>(null);
    const [, forceTick] = useState(0);

    // Publish local pointer position.
    useEffect(() => {
        if (!session || !activeField) return;
        let last = 0;
        const onMove = (event: MouseEvent) => {
            const now = Date.now();
            if (now - last < PUBLISH_INTERVAL_MS) return;
            last = now;
            const overlay = overlayRef.current;
            const scrollEl = overlay?.parentElement?.querySelector("[data-collab-scroll]");
            if (!overlay || !scrollEl) return;
            const fractions = toFractions(event.clientX, event.clientY, overlay.getBoundingClientRect(), readScroll(scrollEl));
            session.provider.setAwarenessField(
                "pointer",
                fractions ? { ...fractions, sectionField: activeField, t: now } : undefined
            );
        };
        window.addEventListener("mousemove", onMove);
        return () => {
            window.removeEventListener("mousemove", onMove);
            session.provider.setAwarenessField("pointer", undefined);
        };
    }, [session, activeField]);

    // Re-render periodically so stale pointers fade out even without updates.
    // Gated on an active collab session — no background timer when collab is off.
    useEffect(() => {
        if (!session || !activeField) return;
        const interval = setInterval(() => forceTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [session, activeField]);

    if (!session || !activeField) return null;

    const overlay = overlayRef.current;
    const scrollEl = overlay?.parentElement?.querySelector("[data-collab-scroll]");
    const rect = overlay?.getBoundingClientRect();
    const scroll = scrollEl ? readScroll(scrollEl) : null;
    const now = Date.now();

    return (
        <div ref={overlayRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 20 }}>
            {rect &&
                scroll &&
                peers
                    .filter(p => p.user && p.pointer && p.pointer.sectionField === activeField && now - p.pointer.t < POINTER_TTL_MS)
                    .map(peer => {
                        const { x, y } = toPixels(peer.pointer!.xf, peer.pointer!.yf, rect, scroll);
                        return (
                            <div key={peer.clientId} style={{ position: "absolute", left: x, top: y, transition: "left 60ms linear, top 60ms linear" }}>
                                <svg width="14" height="18" viewBox="0 0 14 18" style={{ display: "block" }}>
                                    <path d="M0 0 L14 10 L7.5 10.8 L10 17 L7.5 18 L5 11.5 L0 15 Z" fill={peer.user!.color} stroke="#fff" strokeWidth="1" />
                                </svg>
                                <span
                                    style={{
                                        marginLeft: 10,
                                        background: peer.user!.color,
                                        color: "#fff",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "1px 5px",
                                        borderRadius: 3,
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {peer.user!.name}
                                </span>
                            </div>
                        );
                    })}
        </div>
    );
};

export default PointerOverlay;
